import { Inject, Injectable } from '@angular/core';
import { Observable, filter, finalize, map, of, shareReplay, switchMap, take, tap, throwError } from 'rxjs';
import { LoginResponse } from '../modules/shared/interfaces/login-response.type';
import { HttpClient } from '@angular/common/http';

import { Store } from '@ngrx/store';
import * as AuthActions from '../modules/core/store/actions/auth.actions';
import * as fromApp from '../modules/core/store/app.state';
import * as fromAuth from '../modules/core/store/reducers/auth.reducers';
import { User } from '@app/modules/shared/interfaces/user.type';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  // Single-flight guard for refresh calls so multiple callers share one HTTP request
  private refreshInFlight$: Observable<LoginResponse> | null = null;

  constructor(protected http: HttpClient, private store: Store<fromApp.AppState>) { }


  isLoggedIn() {
    const token = sessionStorage.getItem('accessToken');
    return token != null;
  }

  login(email: string, password: string): Observable<any> {
    // this is a mocked response to be able to test the example
    // console.log('Email:', email, 'Password:', password);
    const body = JSON.stringify({ userName: email, password, })// apiKey: this.appEnv.getConfig().apiKey });

    return this.http.post<LoginResponse>('/entrance/login', body);
  }


  // Current user's roles/permissions, without a full re-login.
  getMe(): Observable<any> {
    return this.http.get('/entrance/me');
  }


  verifyTwoFactorAuthOtp(userId: number, otp: number, token: string): Observable<any> {
    const body = JSON.stringify({ userId, otp, token });
    return this.http.post<LoginResponse>('/entrance/verify-two-factor-auth-otp', body);
  }


  logout(manual = false) {
    this.store.dispatch(AuthActions.logout({ manual }));
  }


  refreshAccessToken(): Observable<any> {
    let body = JSON.stringify({ refreshToken: '' });
    return this.store.select('auth').pipe(
      take(1),
      switchMap((authState: fromAuth.State) => {
        if (authState && authState.refreshToken) {
          body = JSON.stringify({ refreshToken: authState.refreshToken });
          return this.http.post<LoginResponse>('/entrance/refresh-login-token', body);
        } else {
          return this.http.post<LoginResponse>('/entrance/refresh-login-token', { refreshToken: '' });
        }
      })
    );
  }

  // Shared refresh helper so multiple consumers (interceptor, socket, etc.) do not duplicate requests
  refreshIfNeeded(): Observable<LoginResponse> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    return this.store.select('auth').pipe(
      take(1),
      switchMap((authState: fromAuth.State) => {
        if (authState.refreshTokenInProgress) {
          return this.waitForExistingRefresh();
        }

        this.store.dispatch(AuthActions.refreshTokenInProgress());

        const refresh$ = this.refreshAccessToken().pipe(
          tap((tokenData: LoginResponse) => this.store.dispatch(AuthActions.refreshTokenSuccess(tokenData))),
          finalize(() => {
            this.refreshInFlight$ = null;
          }),
          shareReplay(1)
        );

        this.refreshInFlight$ = refresh$;
        return refresh$;
      })
    );
  }

  private waitForExistingRefresh(): Observable<LoginResponse> {
    return this.store.select('auth').pipe(
      // Wait until the in-flight refresh is no longer active.
      // Note: the logout reducer sets refreshTokenInProgress to null (not false),
      // so we must accept both false and null as "refresh no longer in progress".
      filter(state => state.refreshTokenInProgress !== true),
      take(1),
      switchMap(state => {
        // If logout occurred during the refresh (accessToken cleared), propagate
        // an error so callers (e.g. the 401 interceptor) can fail fast instead
        // of hanging forever waiting for a token that will never arrive.
        if (!state.accessToken || !state.refreshToken || !state.user) {
          return throwError(() => new Error('Session ended while waiting for token refresh'));
        }
        return of({
          accessToken: state.accessToken as string,
          refreshToken: state.refreshToken as string,
          user: state.user as User,
          roles: state.loggedInModule ? [state.loggedInModule] : []
        });
      })
    );
  }


  verifyUser(data: any): Observable<any> {
    return this.http.post<LoginResponse>('/api/v1/user/verify-and-update', data);
  }

  recoverPassword(data: any): Observable<any> {
    return this.http.post('/api/v1/entrance/recover-password', data);
  }

  verifyForgotPassword(data: any): Observable<any> {
    return this.http.post('/api/v1/entrance/verify-update-password', data);
  }

  updateProfile(data: any): Observable<any> {
    return this.http.post('/api/v1/user/update-profile', data);
  }

  /*FORGOT PASSWORD */
  forgotPassword(data: any): Observable<any> {
    return this.http.post('/entrance/forgot-password', JSON.stringify(data))
  }

  /*RESET PASSWORD */
  resetPassword(data: any): Observable<any> {
    return this.http.post('/entrance/reset-password', JSON.stringify(data))
  }

  /*CHANGE PASSWORD */
  changePassword(data: any): Observable<any> {
    return this.http.post(`/entrance/change-password`, data);
  }


  resendVerificationEmail(data: any): Observable<any> {
    return this.http.post(`/entrance/resend-verification-email`, data)
  }

  verifyEmail(token: string): Observable<any> {
    return this.http.get(`/entrance/verify-user-email/${token}`)
  }

  getUserDetails(id: number): Observable<any> {
    return this.http.get(`/entrance/get-user-details/${id}`);
  }

  signup(data: any): Observable<any> {
    return this.http.post('/entrance/create-user', JSON.stringify(data))
  }

  getSignUpFormDataByUUID(uuid: string): Observable<any> {
    return this.http.post("/entrance/get-sign-up-form-data-by-uuid", JSON.stringify({ uuid }));
  }

}
